import type { ReactNode } from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  getSourceTables,
  postSourceTableSync,
  useGetAllRuns,
  useGetProfilesById,
  useDeleteSourceTablesById,
  usePostSourceTableSync,
  usePutSourceTableState,
} from '@/api/control/control-plane/control-plane';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDeleteDialog } from '@/components/ui/ConfirmDeleteDialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Trash2, RotateCcw, RefreshCw, AlertTriangle, MoreVertical, Plus, Settings, Search, Eye, Network } from 'lucide-react';
import { CreateSourceTableDialog } from './CreateSourceTableDialog';
import { SourceCapacityNotice } from './SourceCapacityNotice';
import { SyncSettingsDialog } from './SyncSettingsDialog';
import { TargetSchemaDialog } from './TargetSchemaDialog';
import { SetParentDialog } from './SetParentDialog';
import { readParentSourceTableId } from '@/api/source-table-parent';
import { CardHeaderActions, type ActionItem } from '@/components/common/CardHeaderActions';
import { useRoles } from '@/contexts/RolesContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useError } from '@/contexts/ErrorContext';
import { toast } from 'sonner';
import {
  SyncRequestSyncMode,
  SourceTableDisabledReason,
  type SourceTableDto,
  type BusinessCentralSourceTableDto,
  type ContinuationToken,
  type ListSourceTablesRequest,
} from '@/api/control/missionImpactAPI.schemas';
import { SyncConfirmationDialog } from '@/components/profiles/SyncConfirmationDialog';
import { ACTIVE_RUN_STATES, getActiveRunTableIds } from '@/lib/activeRuns';


/** How often to re-check for queued or running initial syncs while any table is awaiting one, in milliseconds. */
const ACTIVE_RUNS_REFRESH_MS = 30_000;

/** The maximum number of queued or running runs fetched to find tables whose initial sync is in progress. */
const ACTIVE_RUNS_PAGE_SIZE = 100;

/**
 * Cadence between automatic inventory syncs for Business Central tables that
 * lack an isDeleted column. Mirrors the worker's
 * <c>OrchestrationOptions.InventorySyncIntervalDays</c> default so the UI can
 * project a "next inventory sync" timestamp without an extra API round-trip.
 * Update this if the worker default ever changes.
 */
const INVENTORY_SYNC_INTERVAL_DAYS = 7;

/**
 * Returns <c>true</c> when the table is a Business Central table that uses
 * the inventory-sync deletion strategy (Strategy 2: no isDeleted column).
 * Inventory syncs are only meaningful — and only allowed by the backend —
 * for these tables.
 */
function usesInventoryDeletionStrategy(
  table: SourceTableDto,
): table is BusinessCentralSourceTableDto {
  return table.$type === 'BusinessCentral' && table.hasIsDeletedField === false;
}

/**
 * Computes the projected next inventory sync time from a BC table's last
 * successful inventory sync. Returns <c>null</c> if no inventory sync has
 * ever run, in which case the next run is "as soon as possible" and the UI
 * surfaces that as a separate state.
 */
function projectNextInventorySync(table: BusinessCentralSourceTableDto): Date | null {
  if (!table.lastInventorySyncUtc) return null;
  const last = new Date(table.lastInventorySyncUtc);
  return new Date(last.getTime() + INVENTORY_SYNC_INTERVAL_DAYS * 24 * 60 * 60 * 1000);
}

/**
 * Minimal `{placeholder}` substitution for translation strings. Replaces each
 * key with its stringified value; unmatched placeholders are left as-is.
 */
function format(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? vars[key] : match,
  );
}

/**
 * Renders a translation template containing a single <c>{field}</c>
 * placeholder, substituting the field name as a monospaced <code> element.
 */
function renderViaField(template: string, field: string): ReactNode {
  const [before, after = ''] = template.split('{field}');
  return (
    <>
      {before}
      <code className="font-mono">{field}</code>
      {after}
    </>
  );
}

/**
 * Props for the SourceTablesTable component
 */
interface SourceTablesTableProps {
  /** Organization ID */
  orgId: string;
  /** Profile ID */
  profileId: string;
  /** Source ID */
  sourceId: string;
  /** Source type discriminator, used to build polymorphic add-table requests. */
  sourceType: string;
}

/**
 * Renders text with truncation. Shows a tooltip on hover only when the text
 * is actually overflowing (i.e. the ellipsis is visible).
 */
function TruncatedText({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
    const observer = new ResizeObserver(checkTruncation);
    if (ref.current) observer.observe(ref.current);
    return () => { observer.disconnect(); };
  }, [checkTruncation, text]);

  const span = <span ref={ref} className="truncate block">{text}</span>;

  if (!isTruncated) return span;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{span}</TooltipTrigger>
        <TooltipContent>{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Table component for displaying source tables with data fetching
 */
export function SourceTablesTable({ orgId, profileId, sourceId, sourceType }: SourceTablesTableProps) {
  const queryClient = useQueryClient();

  /**
   * Refreshes the run lists so a sync that was just requested shows as in progress.
   */
  const refreshRuns = () => {
    void queryClient.invalidateQueries({ queryKey: [`/orgs/${orgId}/runs/list`] });
  };

  // Queued or running runs are looked up over the last day, like the profile run summary.
  const activeRunsStartDate = useMemo(() => {
    const oneDayAgo = new Date();
    oneDayAgo.setHours(oneDayAgo.getHours() - 24);
    return oneDayAgo.toISOString();
  }, []);
  const { isAdminOrOperator } = useRoles();
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const [createTableDialogOpen, setCreateTableDialogOpen] = useState(false);
  const [syncSettingsDialogOpen, setSyncSettingsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<string | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState<string | null>(null);
  const [schemaTable, setSchemaTable] = useState<SourceTableDto | null>(null);
  const [parentDialogTable, setParentDialogTable] = useState<SourceTableDto | null>(null);
  const [togglingTableId, setTogglingTableId] = useState<string | null>(null);
  const [syncingTableId, setSyncingTableId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch profile to check if it's enabled
  const { data: profileData, refetch: refetchProfile } = useGetProfilesById(orgId, profileId, {
    query: {
      enabled: !!profileId,
    },
  });

  // Debounce the search term so we don't fire a request on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => { setDebouncedSearch(searchTerm); }, 300);
    return () => { clearTimeout(handle); };
  }, [searchTerm]);

  // Fetch source tables page-by-page using infinite scroll
  const {
    data: sourceTablesData,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['source-tables', sourceId, debouncedSearch],
    queryFn: async ({ pageParam }) => {
      const request: ListSourceTablesRequest = {
        pageSize: 50,
        search: debouncedSearch || undefined,
        continuationToken: pageParam ?? undefined,
      };
      const response = await getSourceTables(orgId, profileId, sourceId, request);
      return response.data;
    },
    initialPageParam: null as ContinuationToken | null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.continuationToken ?? null) : null,
    enabled: !!sourceId,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  // Sentinel ref for IntersectionObserver-based infinite scroll
  const sentinelRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => { observer.disconnect(); };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { mutate: deleteSourceTable, isPending: isDeleting } = useDeleteSourceTablesById({
    mutation: {
      onSuccess: () => {
        setDeleteDialogOpen(null);
        toast.success(t.profiles.sourceTables.successDeleted);
        void refetch();
      },
      onError: (error) => {
        handleApiError(error, { context: 'SourceTablesTable.deleteSourceTable', profile: profileData?.data });
      },
    },
  });

  const { mutate: resetSourceTable, isPending: isResetting } = usePostSourceTableSync({
    mutation: {
      onSuccess: () => {
        setResetDialogOpen(null);
        toast.success(t.profiles.sourceTables.successReset);
        refreshRuns();
        void refetch();
      },
      onError: (error) => {
        handleApiError(error, { context: 'SourceTablesTable.resetSourceTable', profile: profileData?.data });
      },
    },
  });

  const { mutate: updateSourceTableState } = usePutSourceTableState({
    mutation: {
      onSuccess: (_, variables) => {
        setTogglingTableId(null);
        void queryClient.invalidateQueries({ queryKey: ['source-tables', sourceId] });
        toast.success(
          variables.data.isEnabled
            ? t.profiles.sourceTables.tableEnabledSuccessfully
            : t.profiles.sourceTables.tableDisabledSuccessfully
        );
        void refetch();
      },
      onError: (error) => {
        setTogglingTableId(null);
        handleApiError(error, { context: 'SourceTablesTable.updateSourceTableState', profile: profileData?.data });
      },
    },
  });

  const { mutate: syncSourceTable } = usePostSourceTableSync({
    mutation: {
      onSuccess: () => {
        setSyncingTableId(null);
        toast.success(t.profiles.sourceTables.syncTriggeredSuccessfully);
        refreshRuns();
        void refetch();
      },
      onError: (error) => {
        setSyncingTableId(null);
        handleApiError(error, { context: 'SourceTablesTable.syncSourceTable', profile: profileData?.data });
      },
    },
  });

  const sourceTables: SourceTableDto[] = (
    sourceTablesData?.pages.flatMap((page) => page.items ?? []) ?? []
  );
  // While a table's initial sync is queued or running, its Initial Sync action is disabled so the
  // sync is not requested a second time (the worker also ignores such a duplicate request).
  const awaitingInitialSyncTableIds = sourceTables
    .filter((tbl) => !tbl.hasInitialSyncCompleted)
    .map((tbl) => tbl.id);
  const { data: activeRunsData } = useGetAllRuns(
    orgId,
    {
      pageSize: ACTIVE_RUNS_PAGE_SIZE,
      profileIds: [profileId],
      sourceTableIds: awaitingInitialSyncTableIds,
      states: ACTIVE_RUN_STATES,
      startDate: activeRunsStartDate,
    },
    {
      query: {
        enabled: awaitingInitialSyncTableIds.length > 0 && !!orgId && !!profileId,
        refetchInterval: ACTIVE_RUNS_REFRESH_MS,
      },
    },
  );
  const activeRunTableIds = getActiveRunTableIds(activeRunsData?.data.items);
  // Loaded tables keyed by id, used to render a child table's gating-parent name inline (#442).
  const tablesById = new Map(sourceTables.map((tbl) => [tbl.id, tbl]));
  const isProfileEnabled = profileData?.data.isEnabled ?? true;
  const hasAutoDisabledTables = sourceTables.some(
    (table) =>
      !table.isEnabled &&
      table.disabledReason === SourceTableDisabledReason.PersistentFailure
  );
  const hasNotSupportedTables = sourceTables.some(
    (table) =>
      !table.isEnabled &&
      table.disabledReason === SourceTableDisabledReason.NotSupported
  );
  const hasChangeTrackingRequiredTables = sourceTables.some(
    (table) =>
      !table.isEnabled &&
      table.disabledReason === SourceTableDisabledReason.ChangeTrackingRequired
  );

  // Show the BC-specific Sync Mode and Deletions columns when at least one
  // configured table is a Business Central table. Sources are single-type in
  // practice, so Dataverse-only sources will hide both columns cleanly.
  const showBcColumns = sourceTables.some((table) => table.$type === 'BusinessCentral');

  /**
   * Handles deleting a source table
   */
  const handleDeleteTable = (sourceTableId: string) => {
    deleteSourceTable({ orgId, profileId, sourceId, sourceTableId });
  };

  /**
   * Handles dropping and resetting a source table
   */
  const handleResetTable = (sourceTableId: string) => {
    resetSourceTable({
      orgId,
      profileId,
      sourceId,
      sourceTableId,
      data: { syncMode: SyncRequestSyncMode.ResetAndFull },
    });
  };

  /**
   * Handles toggling the enabled/disabled state
   */
  const handleToggleState = (table: SourceTableDto, checked: boolean) => {
    setTogglingTableId(table.id);
    updateSourceTableState({
      orgId,
      profileId,
      sourceId,
      sourceTableId: table.id,
      data: {
        isEnabled: checked,
      },
    });
  };

  /**
   * Handles triggering a sync with the specified mode
   */
  const handleSync = (sourceTableId: string, syncMode: SyncRequestSyncMode) => {
    setSyncingTableId(sourceTableId);
    syncSourceTable({
      orgId,
      profileId,
      sourceId,
      sourceTableId,
      data: {
        syncMode,
      },
    });
  };

  // Build header actions
  const headerActions: ActionItem[] = isAdminOrOperator
    ? [
        {
          key: 'add-table',
          label: t.sources.sourceTablesDialog.buttonText,
          icon: Plus,
          onClick: () => { setCreateTableDialogOpen(true); },
          tooltip: t.sources.sourceTablesDialog.buttonText,
        },
        {
          key: 'sync-settings',
          label: t.profiles.syncSettings.buttonText,
          icon: Settings,
          onClick: () => { setSyncSettingsDialogOpen(true); },
          tooltip: t.profiles.syncSettings.buttonText,
          hidden: !profileData?.data,
        },
      ]
    : [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold">{t.profiles.sourceTables.title}</h3>
          <p className="text-xs text-muted-foreground">{t.profiles.sourceTables.description}</p>
        </div>
        <CardHeaderActions actions={headerActions} />
      </div>

      {/* Dialogs rendered separately in controlled mode */}
      <CreateSourceTableDialog
        orgId={orgId}
        profileId={profileId}
        sourceId={sourceId}
        sourceType={sourceType}
        tablePrefix={profileData?.data.target?.tablePrefix ?? undefined}
        onTableCreated={async (sourceTableId) => {
          // Only trigger an initial sync when the profile is active — inactive
          // profiles reject sync requests, which would block the dialog from
          // closing.
          if (!isProfileEnabled) return;
          await postSourceTableSync(orgId, profileId, sourceId, sourceTableId, {
            syncMode: SyncRequestSyncMode.ResetAndFull,
          });
          refreshRuns();
        }}
        onTablesCreated={() => { void refetch(); }}
        externalOpen={createTableDialogOpen}
        onExternalOpenChange={setCreateTableDialogOpen}
      />
      {profileData?.data && (
        <SyncSettingsDialog
          orgId={orgId}
          profile={profileData.data}
          onSuccess={() => { void refetchProfile(); }}
          externalOpen={syncSettingsDialogOpen}
          onExternalOpenChange={setSyncSettingsDialogOpen}
        />
      )}

      {/* Auto-Disabled Alert */}
      {hasAutoDisabledTables && (
        <Alert className="border-warning background-warning text-warning">
          <AlertTriangle className="text-warning" />
          <AlertTitle>{t.profiles.sourceTables.autoDisabledTitle}</AlertTitle>
          <AlertDescription className="text-warning">
            {t.profiles.sourceTables.autoDisabledDescription}
          </AlertDescription>
        </Alert>
      )}
      {/* Not-Supported Alert */}
      {hasNotSupportedTables && (
        <Alert className="border-warning background-warning text-warning">
          <AlertTriangle className="text-warning" />
          <AlertTitle>{t.profiles.sourceTables.notSupportedTitle}</AlertTitle>
          <AlertDescription className="text-warning">
            {t.profiles.sourceTables.notSupportedDescription}
          </AlertDescription>
        </Alert>
      )}
      {/* Dataverse request limit: queued-runs banner and many-tables hint (#474) */}
      <SourceCapacityNotice
        orgId={orgId}
        profileId={profileId}
        sourceType={sourceType}
        tableCount={debouncedSearch ? 0 : sourceTables.length}
        hasMoreTables={!debouncedSearch && hasNextPage}
      />
      {/* Change-Tracking-Required Alert */}
      {hasChangeTrackingRequiredTables && (
        <Alert className="border-warning background-warning text-warning">
          <AlertTriangle className="text-warning" />
          <AlertTitle>{t.profiles.sourceTables.changeTrackingRequiredTitle}</AlertTitle>
          <AlertDescription className="text-warning">
            {t.profiles.sourceTables.changeTrackingRequiredDescription}
          </AlertDescription>
        </Alert>
      )}
      {isLoading ? (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="max-w-[320px] w-[320px]">{t.profiles.sourceTables.logicalName}</TableHead>
                <TableHead className="w-[140px]">{t.profiles.sourceTables.status}</TableHead>
                <TableHead className="w-[180px]">
                  {t.profiles.sourceTables.initialSyncCompleted}
                </TableHead>
                {isAdminOrOperator && (
                  <TableHead className="w-[100px]">{t.profiles.sourceTables.actions}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3].map((i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-40" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-6 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-6 w-16" />
                  </TableCell>
                  {isAdminOrOperator && (
                    <TableCell>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : sourceTables.length === 0 && !debouncedSearch ? (
        <div className="text-center text-muted-foreground py-8 border rounded-md">
          <p>{t.profiles.sourceTables.noTablesConfigured}</p>
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden [&>[data-slot=table-container]]:max-h-[600px] [&>[data-slot=table-container]]:overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className='sticky top-0 z-10 bg-background border-b'>
                <TableHead className="max-w-[320px] w-[320px]">
                  <div className="flex items-center gap-1.5">
                    <Search className="h-3.5 w-3.5 shrink-0" />
                    <Input
                      placeholder={t.profiles.sourceTables.logicalName}
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); }}
                      className="h-auto w-full border-none bg-transparent dark:bg-transparent p-0 shadow-none font-medium text-foreground focus-visible:ring-0 placeholder:text-foreground focus:placeholder:text-transparent"
                    />
                  </div>
                </TableHead>
                <TableHead className="w-[140px]">{t.profiles.sourceTables.status}</TableHead>
                <TableHead className="w-[180px]">
                  {t.profiles.sourceTables.initialSyncCompleted}
                </TableHead>
                {showBcColumns && (
                  <>
                    <TableHead className="w-[220px]">
                      {t.profiles.sourceTables.syncMode}
                    </TableHead>
                    <TableHead className="w-[260px]">
                      {t.profiles.sourceTables.deletions}
                    </TableHead>
                  </>
                )}
                {isAdminOrOperator && (
                  <TableHead className="w-[100px]">{t.profiles.sourceTables.actions}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sourceTables.length === 0 && debouncedSearch ? (
                <TableRow>
                  <TableCell
                    colSpan={3 + (showBcColumns ? 2 : 0) + (isAdminOrOperator ? 1 : 0)}
                    className="text-center text-muted-foreground py-8"
                  >
                    {t.profiles.sourceTables.noSearchResults}
                  </TableCell>
                </TableRow>
              ) : sourceTables.map((table) => {
                const isToggling = togglingTableId === table.id;
                const isSyncing = syncingTableId === table.id;
                const isAutoDisabled =
                  !table.isEnabled &&
                  table.disabledReason === SourceTableDisabledReason.PersistentFailure;
                const isNotSupported =
                  !table.isEnabled &&
                  table.disabledReason === SourceTableDisabledReason.NotSupported;
                const isChangeTrackingRequired =
                  !table.isEnabled &&
                  table.disabledReason === SourceTableDisabledReason.ChangeTrackingRequired;
                const isSyncDisabled = !table.isEnabled || !isProfileEnabled || isSyncing;
                const isInitialSyncInProgress =
                  !table.hasInitialSyncCompleted && activeRunTableIds.has(table.id);
                const inventoryStrategyTable = usesInventoryDeletionStrategy(table) ? table : null;
                const nextInventorySyncDate = inventoryStrategyTable
                  ? projectNextInventorySync(inventoryStrategyTable)
                  : null;
                // Inventory sync is only valid for BC tables that lack an
                // isDeleted column AND have completed at least one initial sync.
                const canTriggerInventorySync =
                  inventoryStrategyTable !== null && table.hasInitialSyncCompleted;

                // Sync Mode cell content — differs by source type and by
                // whether a BC table has a timestamp watermark field. The
                // field name is surfaced as an optional subtitle under the
                // primary label.
                let syncModeLabel: string;
                let syncModeSubtext: ReactNode = null;
                let syncModeTooltip: string;
                if (table.$type === 'BusinessCentral') {
                  if (table.timestampField) {
                    syncModeLabel = t.profiles.sourceTables.syncModeIncremental;
                    syncModeSubtext = renderViaField(
                      t.profiles.sourceTables.viaFieldLabel,
                      table.timestampField,
                    );
                    syncModeTooltip = format(
                      t.profiles.sourceTables.syncModeIncrementalOnTooltip,
                      { field: table.timestampField },
                    );
                  } else {
                    syncModeLabel = t.profiles.sourceTables.syncModeFull;
                    syncModeTooltip = t.profiles.sourceTables.syncModeFullTooltip;
                  }
                } else {
                  syncModeLabel = t.profiles.sourceTables.syncModeIncremental;
                  syncModeTooltip = t.profiles.sourceTables.syncModeIncrementalTooltip;
                }

                // Deletions cell content — reflects the actual deletion
                // reconciliation strategy for the table. The optional
                // subtext surfaces supplemental info (e.g. next inventory
                // sync date) under the primary label.
                let deletionsLabel: string;
                let deletionsSubtext: ReactNode = null;
                let deletionsTooltip: string;
                if (table.$type === 'BusinessCentral') {
                  if (table.hasIsDeletedField === true) {
                    const fieldName = table.isDeletedFieldName ?? 'isDeleted';
                    deletionsLabel = t.profiles.sourceTables.deletionsViaField;
                    deletionsSubtext = renderViaField(
                      t.profiles.sourceTables.viaFieldLabel,
                      fieldName,
                    );
                    deletionsTooltip = format(
                      t.profiles.sourceTables.deletionsViaFieldTooltip,
                      { field: fieldName },
                    );
                  } else if (table.hasIsDeletedField === false) {
                    deletionsLabel = t.profiles.sourceTables.deletionsViaInventory;
                    deletionsSubtext = nextInventorySyncDate
                      ? format(t.profiles.sourceTables.deletionsViaInventoryNextDate, {
                          date: nextInventorySyncDate.toLocaleDateString(),
                        })
                      : t.profiles.sourceTables.deletionsViaInventoryPendingInitialSync;
                    deletionsTooltip = t.profiles.sourceTables.deletionsViaInventoryTooltip;
                  } else {
                    deletionsLabel = t.profiles.sourceTables.deletionsPending;
                    deletionsTooltip = t.profiles.sourceTables.deletionsPendingTooltip;
                  }
                } else {
                  deletionsLabel = t.profiles.sourceTables.deletionsViaChangeTracking;
                  deletionsTooltip = t.profiles.sourceTables.deletionsViaChangeTrackingTooltip;
                }

                // Display name is normalized server-side (blank -> null), so it is either a
                // non-empty string or nullish here; fall back to the logical name when absent.
                const trimmedDisplayName = table.displayName?.trim();
                const primaryName = trimmedDisplayName ?? table.logicalName;
                const showLogicalName =
                  trimmedDisplayName !== undefined && trimmedDisplayName !== table.logicalName;

                // Gating parent (#442): resolve the parent's friendly name from the loaded tables so
                // a child row can show "Child of <parent>". Falls back to the raw id if the parent is
                // on a not-yet-loaded page.
                const parentId = readParentSourceTableId(table);
                const parentTable = parentId !== null ? tablesById.get(parentId) : undefined;
                const parentLabel = parentTable
                  ? (parentTable.displayName?.trim() ?? '') || parentTable.logicalName
                  : (parentId ?? '');

                return (
                  <TableRow key={table.id}>
                    <TableCell className="max-w-[320px]">
                      <div className="flex flex-col">
                        <TruncatedText text={primaryName} />
                        {showLogicalName && (
                          <span className="truncate text-xs text-muted-foreground">
                            {table.logicalName}
                          </span>
                        )}
                        {parentId !== null && (
                          <span className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                            <Network className="h-3 w-3 shrink-0" />
                            {format(t.profiles.sourceTables.childOf, { name: parentLabel })}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {isAutoDisabled ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="background-warning text-warning border-warning cursor-help"
                              >
                                <AlertTriangle className="h-3 w-3 mr-1 text-warning" />
                                {t.profiles.sourceTables.autoDisabled}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t.profiles.sourceTables.autoDisabledTooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : isNotSupported ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="background-warning text-warning border-warning cursor-help"
                              >
                                <AlertTriangle className="h-3 w-3 mr-1 text-warning" />
                                {t.profiles.sourceTables.notSupported}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t.profiles.sourceTables.notSupportedTooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : isChangeTrackingRequired ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="background-warning text-warning border-warning cursor-help"
                              >
                                <AlertTriangle className="h-3 w-3 mr-1 text-warning" />
                                {t.profiles.sourceTables.changeTrackingRequired}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t.profiles.sourceTables.changeTrackingRequiredTooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Badge
                          variant="outline"
                          className={
                            table.isEnabled
                              ? 'background-success text-success border-success'
                              : 'background-neutral text-neutral border-neutral'
                          }
                        >
                          {table.isEnabled
                            ? t.profiles.sourceTables.enabled
                            : t.profiles.sourceTables.disabled}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          table.hasInitialSyncCompleted
                            ? 'background-success text-success border-success'
                            : 'background-neutral text-neutral border-neutral'
                        }
                      >
                        {table.hasInitialSyncCompleted
                          ? t.profiles.sourceTables.yes
                          : t.profiles.sourceTables.no}
                      </Badge>
                    </TableCell>
                    {showBcColumns && (
                      <>
                        <TableCell className="text-sm">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="cursor-help flex flex-col leading-tight">
                                  <span>{syncModeLabel}</span>
                                  {syncModeSubtext && (
                                    <span className="text-[11px] text-muted-foreground opacity-80">
                                      {syncModeSubtext}
                                    </span>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>{syncModeTooltip}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="text-sm">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="cursor-help flex flex-col leading-tight">
                                  <span>{deletionsLabel}</span>
                                  {deletionsSubtext && (
                                    <span className="text-[11px] text-muted-foreground opacity-80">
                                      {deletionsSubtext}
                                    </span>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>{deletionsTooltip}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                      </>
                    )}
                    {isAdminOrOperator && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {/* Enable/Disable Switch */}
                            <div className="flex items-center justify-between px-2 py-2">
                              <span className="text-sm">
                                {t.profiles.sourceTables.enabledLabel}
                              </span>
                              <Switch
                                id={`table-${table.id}-enabled`}
                                checked={table.isEnabled}
                                disabled={isToggling}
                                onCheckedChange={(checked) => { handleToggleState(table, checked); }}
                              />
                            </div>

                            <DropdownMenuSeparator />

                            {/* Sync Button - ResetAndFull sync if initial sync not completed, Incremental otherwise */}
                            {!table.hasInitialSyncCompleted ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="w-full">
                                    <DropdownMenuItem
                                      disabled={isSyncDisabled || isInitialSyncInProgress}
                                      onSelect={() =>
                                        { handleSync(table.id, SyncRequestSyncMode.ResetAndFull); }
                                      }
                                    >
                                      <RefreshCw
                                        className={`h-4 w-4 mr-2 ${isSyncing || isInitialSyncInProgress ? 'animate-spin' : ''}`}
                                      />
                                      {t.profiles.sourceTables.initialSync}
                                    </DropdownMenuItem>
                                  </span>
                                </TooltipTrigger>
                                {(!isProfileEnabled || !table.isEnabled || isInitialSyncInProgress) && (
                                  <TooltipContent>
                                    {!isProfileEnabled
                                      ? t.profileDetail.actions.syncDisabledTooltip
                                      : !table.isEnabled
                                        ? t.profiles.sourceTables.tableDisabledSyncTooltip
                                        : t.profiles.sourceTables.initialSyncInProgressTooltip}
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="w-full">
                                    <DropdownMenuItem
                                      disabled={isSyncDisabled}
                                      onSelect={() =>
                                        { handleSync(table.id, SyncRequestSyncMode.Incremental); }
                                      }
                                    >
                                      <RefreshCw
                                        className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
                                      />
                                      {t.profiles.sourceTables.incrementalSync}
                                    </DropdownMenuItem>
                                  </span>
                                </TooltipTrigger>
                                {(!isProfileEnabled || !table.isEnabled) && (
                                  <TooltipContent>
                                    {!isProfileEnabled
                                      ? t.profileDetail.actions.syncDisabledTooltip
                                      : t.profiles.sourceTables.tableDisabledSyncTooltip}
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            )}

                            {/* Inventory Sync - Only available for BC tables that lack an
                                isDeleted column (Strategy 2) and have completed initial sync.
                                Reconciles deletes by primary-key presence; the cadence is
                                governed by the worker but can be triggered manually here. */}
                            {canTriggerInventorySync && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="w-full">
                                    <DropdownMenuItem
                                      disabled={isSyncDisabled}
                                      onSelect={() =>
                                        { handleSync(table.id, SyncRequestSyncMode.Inventory); }
                                      }
                                    >
                                      <RefreshCw
                                        className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
                                      />
                                      {t.profiles.sourceTables.inventorySync}
                                    </DropdownMenuItem>
                                  </span>
                                </TooltipTrigger>
                                {(!isProfileEnabled || !table.isEnabled) && (
                                  <TooltipContent>
                                    {!isProfileEnabled
                                      ? t.profileDetail.actions.syncDisabledTooltip
                                      : t.profiles.sourceTables.tableDisabledSyncTooltip}
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            )}

                            {/* Reset and Resync Button - Only show when initial sync is completed */}
                            {table.hasInitialSyncCompleted && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="w-full">
                                    <DropdownMenuItem
                                      disabled={isSyncDisabled}
                                      onSelect={() => { setResetDialogOpen(table.id); }}
                                    >
                                      <RotateCcw className="h-4 w-4 mr-2 text-destructive" />
                                      {t.profiles.sourceTables.resetAndResync}
                                    </DropdownMenuItem>
                                  </span>
                                </TooltipTrigger>
                                {(!isProfileEnabled || !table.isEnabled) && (
                                  <TooltipContent>
                                    {!isProfileEnabled
                                      ? t.profileDetail.actions.syncDisabledTooltip
                                      : t.profiles.sourceTables.tableDisabledSyncTooltip}
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            )}

                            <DropdownMenuSeparator />

                            {/* Set parent table - admin-defined sync ordering (#442). */}
                            <DropdownMenuItem
                              onSelect={(e) => {
                                e.preventDefault();
                                setParentDialogTable(table);
                              }}
                            >
                              <Network className="h-4 w-4 mr-2" />
                              {t.profiles.sourceTables.setParent}
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />

                            {/* View Target Schema - read-only browse of the destination schema. */}
                            <DropdownMenuItem
                              onSelect={(e) => {
                                e.preventDefault();
                                setSchemaTable(table);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              {t.profiles.sourceTables.viewTargetSchema}
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />

                            {/* Delete Button */}
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={(e) => {
                                e.preventDefault();
                                setDeleteDialogOpen(table.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t.profiles.sourceTables.delete}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Delete confirmation dialog */}
                        <ConfirmDeleteDialog
                          trigger={null}
                          title={t.profiles.sourceTables.deleteTitle}
                          description={t.profiles.sourceTables.deleteDescription}
                          confirmationKey={table.logicalName}
                          confirmationLabel={t.profiles.sourceTables.confirmationLabel}
                          warningMessage={t.profiles.sourceTables.deleteWarning}
                          onDelete={() => { handleDeleteTable(table.id); }}
                          isDeleting={isDeleting}
                          open={deleteDialogOpen === table.id}
                          onOpenChange={(open) => { setDeleteDialogOpen(open ? table.id : null); }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {/* Sentinel row triggers loading the next page when scrolled into view */}
              <TableRow ref={sentinelRef} className="h-0 border-0" aria-hidden>
                <TableCell
                  colSpan={3 + (showBcColumns ? 2 : 0) + (isAdminOrOperator ? 1 : 0)}
                  className="p-0 border-0"
                >
                  {isFetchingNextPage && (
                    <div className="flex justify-center py-4">
                      <Skeleton className="h-4 w-40" />
                    </div>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      {/* Reset confirmation dialog */}
      {resetDialogOpen && (
        <SyncConfirmationDialog
          open={!!resetDialogOpen}
          onOpenChange={(open) => { setResetDialogOpen(open ? resetDialogOpen : null); }}
          onConfirm={() => { handleResetTable(resetDialogOpen); }}
          isSyncing={isResetting}
        />
      )}

      {schemaTable && (
        <TargetSchemaDialog
          orgId={orgId}
          profileId={profileId}
          sourceId={sourceId}
          sourceTableId={schemaTable.id}
          logicalName={schemaTable.logicalName}
          hasInitialSyncCompleted={schemaTable.hasInitialSyncCompleted}
          open={!!schemaTable}
          onOpenChange={(open) => { setSchemaTable(open ? schemaTable : null); }}
        />
      )}

      {parentDialogTable && (
        <SetParentDialog
          orgId={orgId}
          profileId={profileId}
          sourceId={sourceId}
          table={parentDialogTable}
          candidates={sourceTables}
          open={!!parentDialogTable}
          onOpenChange={(open) => { setParentDialogTable(open ? parentDialogTable : null); }}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['source-tables', sourceId] });
            void refetch();
          }}
        />
      )}
    </div>
  );
}
